import { useAppStore } from '@/stores/app';
import { RouteAuthContext } from '@/types';

/**
 * Org-aware role checking helpers.
 *
 * Supports:
 * - Allowed roles: Record<string, string[]> (List of roles a user CAN assume)
 * - Assumed roles: Record<string, string>   (The single role currently active)
 */

/**
 * Returns the effective selected organization for use in route loaders and
 * beforeLoad guards.
 *
 * On a hard browser reload the root component's rehydration effect hasn't fired
 * yet when loaders run, so `selectedOrganization` (runtime) is transiently null
 * even for authenticated users. Falling back to `selectedOrgStub` (persisted,
 * available synchronously from Zustand) avoids errant redirects to /organizations.
 */
export function getSelectedOrg() {
  const store = useAppStore.getState();
  return store.selectedOrganization ?? store.selectedOrgStub;
}

/**
 * Checks if the user has a required role in a given org.
 * Supports both array of roles (allowed roles) and single role (assumed role).
 *
 * @param roles - Role map (either allowed roles array or assumed single role)
 * @param orgId - Organization ID to check
 * @param requiredRole - Role to check for
 * @returns true if the user has the required role
 */
export function hasOrgRole(
  roles: Record<string, string[] | string> | undefined,
  orgId: string | undefined,
  requiredRole: string,
): boolean {
  if (!roles || !orgId) return false;

  const roleOrRoles = roles[orgId];

  if (Array.isArray(roleOrRoles)) {
    return roleOrRoles.includes(requiredRole);
  }

  return roleOrRoles === requiredRole;
}

/**
 * Checks if the user has any of the required roles in a given org.
 * Supports both array of roles (allowed roles) and single role (assumed role).
 *
 * @param roles - Role map (either allowed roles array or assumed single role)
 * @param orgId - Organization ID to check
 * @param requiredRoles - Array of roles to check for
 * @returns true if the user has any of the required roles
 */
export function hasAnyOrgRole(
  roles: Record<string, string[] | string> | undefined,
  orgId: string | undefined,
  requiredRoles: string[],
): boolean {
  if (!roles || !orgId || !requiredRoles.length) return false;

  const roleOrRoles = roles[orgId];

  if (Array.isArray(roleOrRoles)) {
    return requiredRoles.some(role => roleOrRoles.includes(role));
  }

  return typeof roleOrRoles === 'string' && requiredRoles.includes(roleOrRoles);
}

/**
 * Shared route guard logic for super-user / org-admin access.
 * Throws a redirect to /unauthorized if the user does not have any admin role in the selected org.
 * Optionally sets sessionStorage['unauthorized_referrer'] to the current path.
 *
 * @param context - The route context containing authentication and user info
 * @param location - The route location object (must have pathname)
 * @param selectedOrganization - The currently selected organization (runtime, full object) or minimal stub
 * @param adminRoles - Array of admin roles (e.g., from @/constants)
 * @param redirect - Redirect function (e.g., from @tanstack/react-router)
 */
export function checkAndRedirectForOrgAdminAccess({
  context,
  location,
  selectedOrganization,
  adminRoles,
  redirect,
}: {
  context: RouteAuthContext;
  location: { pathname: string };
  selectedOrganization: { id: string } | undefined;
  adminRoles: string[];
  redirect: (opts: { to: string }) => unknown;
}) {
  // Wait for auth to resolve — don't make redirect decisions mid-transition.
  if (context.authentication.status === 'PENDING') {
    return;
  }

  // Any state other than AUTHENTICATED sends the user to login, not to
  // /unauthorized, which would be a dead end for an unauthenticated user.
  if (context.authentication.status !== 'AUTHENTICATED') {
    throw redirect({ to: '/login' });
  }

  // From here status === 'AUTHENTICATED'; org/role checks are meaningful.
  if (!selectedOrganization) {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('unauthorized_referrer', location.pathname);
    }
    throw redirect({ to: '/unauthorized' });
  }

  // Fail closed: every other unresolved state above redirects, so an AUTHENTICATED status with no
  // user must not be the one path that falls through this admin gate and grants access.
  if (
    !context.authentication.user ||
    !hasAnyOrgRole(context.authentication.user.assumed_roles, selectedOrganization.id, adminRoles)
  ) {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('unauthorized_referrer', location.pathname);
    }
    throw redirect({ to: '/unauthorized' });
  }
}
